import {
  Ban,
  BarChart3,
  Clock,
  Coins,
  Flag,
  Gauge,
  LogOut,
  MapPin,
  MessageSquareWarning,
  PieChart,
  Swords,
  Target,
} from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { HistoBars, PanelGrid, RankBars, RateBars, StackShare } from '@/components/charts';
import { Donut } from '@/components/Donut';
import { AxisHeadline, Notice, Panel } from '@/components/Panel';
import { RUN_WINDOW, STUCK_FLOOR, useLensData } from '@/data/source';
import { useAppState, useSettings } from '@/data/settings';
import type { EventRow } from '@/lens';
import {
  lensBlockedDist,
  lensBlockedDonutSeries,
  lensBlockedRate,
  lensBlockedSplit,
  lensCheckDcDist,
  lensCheckDcNotice,
  lensCheckOutcomeDist,
  lensCheckVerbStats,
  lensCompletionRate,
  lensCostByChapter,
  lensCostPerRun,
  lensEndingDist,
  lensEndingNotice,
  lensExitReasonDist,
  lensFormatKrw,
  lensFormatPct,
  lensLatencyDist,
  lensLatencyP95,
  lensLatencySummary,
  lensNarrationDist,
  lensNarrationNotice,
  lensNarrationRate,
  lensSourceBadge,
  lensSchemaNotice,
  lensStartSceneNotice,
  lensStatusFor,
  lensStuckNotice,
  lensStuckPoints,
  lensVerbDist,
} from '@/lens';

const AXIS = {
  completion: 'var(--lens-axis-completion)',
  blocked: 'var(--lens-axis-blocked)',
  narration: 'var(--lens-axis-narration)',
  cost: 'var(--lens-axis-cost)',
} as const;

/**
 * 4축 화면 넷.
 *
 * ★★ **개요와 같은 함수·같은 인자**를 부른다 — 두 화면이 다른 값을 보이면
 * 그건 **진짜 버그**다.
 *
 * ⚠️ 각 화면이 부르는 집계는 원본에서 긁어 맞췄다:
 *   `grep -ohE "lens[A-Z][A-Za-z0-9]+" generated_code/lib/<page>/*.dart`
 */

/**
 * 격자 한 칸 — ★ **배너를 자기 패널에 붙여 둔다.**
 *
 * ⚠️ 배너를 격자 밖에 두면 2열로 묶는 순간 **어느 패널에 대한 경고인지**
 * 알 수 없게 된다(옆 칸 패널 위에 걸릴 수 있다).
 */
function Cell({ children }: { children: React.ReactNode }) {
  return <div className="flex min-w-0 flex-col gap-md">{children}</div>;
}

/** 축 화면 공통 껍데기 — 기간 칩 · 소스 배지 · 축 큰 숫자. */
function AxisFrame({
  title,
  subtitle,
  accent,
  render,
}: {
  title: string;
  subtitle: string;
  /** 축색 — ★ 이 화면의 모든 그래프가 이 색을 쓴다. */
  accent: string;
  render: (rows: EventRow[], P: ReturnType<typeof useSettings>['settings']) => React.ReactNode;
}) {
  // ★ 기간은 **화면 사이에 공유**된다 — 원본의 FFAppState().dateRangeKey 짝.
  const { rangeKey, setRangeKey } = useAppState();
  const P = useSettings().settings;
  const { rows, loading, error, mode } = useLensData(rangeKey);

  return (
    <AppShell
      title={title}
      subtitle={subtitle}
      accent={accent}
      rangeKey={rangeKey}
      onRangeChange={setRangeKey}
    >
      {loading ? (
        <p className="text-body-md text-muted-foreground">불러오는 중…</p>
      ) : error !== null ? (
        <Panel tone="error">데이터를 읽지 못했습니다 — {error}</Panel>
      ) : (
        <div className="space-y-xl">
          {/* ⚠️ 배지는 집계가 보는 목록이 확정된 뒤에 만든다. */}
          <div
            className="inline-block rounded-pill px-lg py-xs text-label-md"
            style={{
              background: `color-mix(in srgb, ${accent} 12%, transparent)`,
              color: accent,
            }}
          >
            {lensSourceBadge(rows, mode, RUN_WINDOW)}
          </div>
          {render(rows, P)}
        </div>
      )}
    </AppShell>
  );
}

// --- 완주율 -----------------------------------------------------------------

export function CompletionPage() {
  return (
    <AxisFrame
      title="완주율"
      accent={AXIS.completion}
      subtitle="run_started 대비 ending_reached 비율입니다. 기준선은 세는 법이 정해지지 않아 판정하지 않습니다 — 값과 변화량으로 읽습니다."
      render={(rows) => (
        <>
          <Notice text={lensStartSceneNotice(rows)} />

          {/* ★★ **짧은 패널을 2열로.** 전에는 전부 한 줄씩 쌓여 가로로 넓고
              세로로 길었다(사용자 지적 — 「한눈에 들어오지 않는다」). */}
          {/* ★★ **짧은 패널을 2열로.** 전에는 전부 한 줄씩 쌓여 가로로 넓고
              세로로 길었다(사용자 지적 — 「한눈에 들어오지 않는다」).
              ★ 구성비 둘(이탈 사유 · 판정 결과)을 나란히 둬 **같은 형태끼리**
              비교되게 했다. */}
          <PanelGrid>
            {/* ★ 큰 숫자도 **격자 한 칸**이다 — 한 줄을 통째로 쓰면 1300px 가
                비어 그만큼 아래 그래프가 화면 밖으로 밀린다(실측). */}
            <AxisHeadline
              accent={AXIS.completion}
              label="완주율"
              icon={Flag}
              value={lensFormatPct(lensCompletionRate(rows))}
              targetText="ending_reached / run_started"
              // ⚠️ 판정하지 않는다 — 세는 법이 정본에 없다.
              status="unrated"
            />

            <StackShare
              color={AXIS.completion}
              title="이탈 사유 분포"
              icon={LogOut}
              delay={1}
              note={
                '단위가 이벤트가 아니라 회차입니다. 「사유 미기록」은 run_started 는 ' +
                '있는데 ending_reached 가 없는 회차에서 사유가 남은 것을 뺀 나머지입니다 — ' +
                '서버에 세션 하트비트가 없어 앱 종료·타임아웃은 발행되지 않습니다.'
              }
              items={lensExitReasonDist(rows)}
            />

            <StackShare
              color={AXIS.completion}
              title="판정 결과"
              icon={PieChart}
              delay={2}
              note="check_resolved 를 굴림 성공 · 굴림 실패 · 우회로 나눴습니다. 우회는 주사위를 굴리지 않고 조건으로 통과한 것이라 DC 와 무관합니다."
              items={lensCheckOutcomeDist(rows)}
            />

            <Cell>
              <Notice text={lensEndingNotice(rows)} />
              <RankBars
                color={AXIS.completion}
                title="결말 분포"
                icon={Target}
                delay={3}
                note={
                  '한 결말에 쏠려 있으면 분기가 실제로는 안 갈린다는 뜻이고, 아무도 못 본 ' +
                  '결말이 있으면 도달 조건이 너무 빡빡하다는 뜻입니다. 갈래는 등급이 아니라 종류입니다. ' +
                  // ⚠️⚠️ **한 칸에 둘이 섞인다.** 결과 규칙에 ending_id 가 없는 끝은
                  //   **장면 id 로 떨어진다**(개발 회신 50 1절 — lensEvents.ts 614~632).
                  //   ★ 실측 2026-10-09 — K-2(결말) 1 · act0_ch4_ridge_dawn 1 ·
                  //     act0_ch4_stub 5 · act1_ch3_stub 3.
                  // ★★ **가르지 않는다** — 결말을 종류로 분류하지 않기로 했고(필드확정
                  //   「놓친 것 ③」), 가르려면 장면 id 접두 규칙을 집계에 넣어야 한다
                  //   (회신 18 2절에서 안 넣기로 했다). ⚠️ 대신 **섞인다는 사실을 밝힌다.**
                  'K- 로 시작하는 것이 작가가 쓴 결말이고, 나머지는 결말 없이 끝난 장면의 ' +
                  'id 입니다. 둘을 가르지 않습니다.'
                }
                items={lensEndingDist(rows)}
              />
            </Cell>

            <RateBars
              color={AXIS.completion}
              title="동사별 판정 성공률"
              icon={Swords}
              delay={4}
              note="막대 길이가 점유율이 아니라 성공률입니다. 낮은 순으로 정렬했으므로 맨 위가 가장 어려운 동사입니다. 우회는 분모에서 뺐습니다."
              items={lensCheckVerbStats(rows)}
            />

            <Cell>
              <Notice text={lensCheckDcNotice(rows)} />
              <HistoBars
                color={AXIS.completion}
                title="DC 구간 분포"
                icon={BarChart3}
                delay={5}
                note={
                  '런타임 DC 는 5~20 에만 존재합니다(MIN_DC 5 · MAX_DC 20 · 장면 파생 보정 ' +
                  '합 상한 5). 0건인 구간도 남깁니다 — 전부 도달 가능한 난도라 0 이 정보입니다.'
                }
                items={lensCheckDcDist(rows)}
              />
            </Cell>
          </PanelGrid>
        </>
      )}
    />
  );
}

// --- 진행 불가 ---------------------------------------------------------------

export function BlockedPage() {
  return (
    <AxisFrame
      title="진행 불가"
      accent={AXIS.blocked}
      subtitle="판정에 도달하지 못한 턴의 비율입니다. 기준선은 세는 법이 정해지지 않아 판정하지 않습니다 — 절대값보다 내려가는 추세와 아래 막힘 지점이 신호입니다."
      render={(rows) => {
        const dist = lensBlockedDist(rows);
        // ⚠️ 도넛 중앙은 **분모**다 — 분자처럼 쓰면 화면이 거짓말한다(실측).
        const turns = dist.reduce((a, d) => a + d.count, 0);
        return (
          <>
            <PanelGrid>
              <AxisHeadline
                accent={AXIS.blocked}
                label="진행 불가"
                icon={Ban}
                value={lensFormatPct(lensBlockedRate(rows))}
                targetText="판정 미도달 / 행동 턴"
                status="unrated"
              />

              <Panel
                title="턴 층 분포"
                icon={PieChart}
                delay={1}
                accent={AXIS.blocked}
                note={
                  '플레이어 입력이 어디로 갔는지 배타 분류로 셉니다. 「막힌 것」은 고칠 ' +
                  '자리이고 「답한 것」은 세계가 답한 턴이라 정상입니다 — 분자에서 빼지 ' +
                  '않고 나눕니다(빼면 「판정 미도달」이라는 이름이 거짓말이 됩니다).'
                }
              >
                {/* ⚠️⚠️ 이 줄은 **설명이 아니라 값**이다 — 툴팁에 숨기면 안 된다.
                    ★ 실측으로 테스트가 잡았다(2026-10-03 개편). */}
                <p className="mb-lg text-body-lg">{lensBlockedSplit(dist)}</p>
              {/* ★ 「막힌 것」 바로 옆이다 — 그 수가 판본에 기대기 때문이다.
                  ⚠️ 판본이 하나로 모이면 배너가 저절로 사라진다. */}
              <Notice text={lensSchemaNotice(rows)} />
                <Donut
                  series={lensBlockedDonutSeries(dist)}
                  centerValue={`${turns}건`}
                  centerLabel="행동 턴(분모)"
                />
              </Panel>

              <RankBars
                color={AXIS.blocked}
                title="동사별 시도 분포"
                icon={Swords}
                delay={2}
                note="거부된 시도도 셉니다 — 규칙 계층이 거부한 턴은 action_interpreted 를 발행하지 않으므로, 앞의 것만 세면 콘텐츠 보강 신호가 통째로 빠집니다."
                items={lensVerbDist(rows)}
              />

              <Cell>
                <Notice text={lensStuckNotice(rows, STUCK_FLOOR)} />
                <RankBars
                  color={AXIS.blocked}
                  title="막힘 지점"
                    icon={MapPin}
                    delay={3}
                  note={
                    '같은 대상에 거부가 3회 이상 쌓인 (장면, 소품)입니다. 막대는 그 장면에 ' +
                    '진입한 회차 중 여기서 막힌 비율이고, 비율 상위 여섯 곳만 보여줍니다. ' +
                    '사람이 그만두는 자리라 진행 불가율을 내리려면 대개 여기부터 손봅니다.'
                  }
                  items={lensStuckPoints(rows, STUCK_FLOOR)}
                />
              </Cell>
            </PanelGrid>
          </>
        );
      }}
    />
  );
}

// --- AI 오서술 ---------------------------------------------------------------

export function NarrationQaPage() {
  return (
    <AxisFrame
      title="AI 오서술"
      accent={AXIS.narration}
      subtitle="서술 검증 실패 사유별 분포. 기준선은 재는 것이 달라 판정하지 않습니다 — 12절의 「상태 모순」은 이 검증기가 보는 다섯 사유에 없습니다."
      render={(rows) => (
        <>
          {/* ★ 패널이 하나뿐이라 **반 폭**으로 둔다 — 꽉 채우면 사유 넷짜리
              막대가 1300px 로 늘어나 읽기만 더 어렵다. */}
          <PanelGrid>
            <AxisHeadline
              accent={AXIS.narration}
              label="AI 오서술"
              icon={MessageSquareWarning}
              value={lensFormatPct(lensNarrationRate(rows))}
              targetText="검증 실패 / 검증이 돈 턴"
              status="unrated"
            />
            <Cell>
              {/* ⚠️⚠️ 「실패가 0건」과 「검증이 안 돎」을 가르는 배너다. */}
              <Notice text={lensNarrationNotice(rows)} />
              <RankBars
                color={AXIS.narration}
                title="검증 실패 사유"
                icon={MessageSquareWarning}
                delay={1}
                note="서술이 허용 목록(현재 장면 소품 · 확정 발견 플래그 · 확정 턴 결과)을 벗어난 경우입니다."
                items={lensNarrationDist(rows)}
              />
            </Cell>
          </PanelGrid>
        </>
      )}
    />
  );
}

// --- 회차당 원가 -------------------------------------------------------------

export function CostPage() {
  return (
    <AxisFrame
      title="회차당 원가"
      accent={AXIS.cost}
      subtitle="목표 600원 · 경고 750원 · 상한 900원. 실측 cost_usd 환산 · 응답 시간."
      render={(rows, P) => {
        const cost = lensCostPerRun(rows, P.costPerTurnKrw, P.usdKrwRate, P.costFeeRate);
        const p95 = lensLatencyP95(rows);
        return (
          <>
            <PanelGrid>
              <AxisHeadline
                accent={AXIS.cost}
                label="회차당 원가"
                icon={Coins}
                value={lensFormatKrw(cost)}
                targetText="목표 600원 · 경고 750원 · 상한 900원"
                // ★ 원가만 판정한다 — 세는 법이 같은 문서에 있다.
                status={lensStatusFor(cost, P.costWarnKrw, P.costCapKrw, false)}
              />
              <AxisHeadline
                accent={AXIS.cost}
                label="응답 시간 P95"
                icon={Gauge}
                value={p95 < 0 ? '데이터 없음' : `${p95.toFixed(1)}초`}
                targetText="경고 5초 · 위반 12초"
                // ★ 판정은 P95 로 한다 — P50 은 거의 항상 초록이라 패널이 존재할
                //   이유가 없어진다(평균을 뺀 것과 같은 이유).
                status={lensStatusFor(p95, P.latencyWarnSec, P.latencyBadSec, false)}
              />

              <RankBars
                color={AXIS.cost}
                title="챕터별 원가"
                icon={Coins}
                delay={1}
                note="회차당 평균입니다. 어느 챕터가 비싼지 봅니다."
                items={lensCostByChapter(rows, P.costPerTurnKrw, P.usdKrwRate, P.costFeeRate)}
              />

              <HistoBars
                color={AXIS.cost}
                title="응답 시간"
                icon={Clock}
                delay={2}
                // ⚠️⚠️ P50·P95·최대·표본은 **값이다** — 툴팁에 숨기면 화면에서
                //   사라진다(`lensBlockedSplit` 과 같은 실수를 두 번째로 했다).
                lead={lensLatencySummary(rows)}
                note={
                  '경계가 기준선(5초 · 12초)과 같습니다 — 어긋나면 막대를 보고 몇 %가 ' +
                  '기준을 넘었는지 셀 수 없습니다. 꼬리 구간은 0건이어도 남깁니다.'
                }
                items={lensLatencyDist(rows)}
              />
            </PanelGrid>
          </>
        );
      }}
    />
  );
}
